import axios, { AxiosResponse } from 'axios';
import type { DecisionStorageKey } from '../lib/lockedSections';

export /**
 * @method post
 * @brief 관리자 회원가입
 */
const createAccountAPI = async (
    adminId: string,
    password: string,
    name: string,
    phoneNumber: string,
    gender: string,
    email: string,
    centerName: string,
    centerCategory: string,
    centerAddress: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/signup`,
            headers: {},
            data: {
                adminId,
                password,
                name,
                phoneNumber,
                gender,
                email,
                centerName,
                centerCategory,
                centerAddress,
            },
        });

        if (response.status === 201) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 409:
                alert('중복된 ID 입니다');
                return { status: error.response.status };
            case 500:
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 관리자 로그인
 */
const requestSignInAPI = async (adminId: string, password: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/signin`,
            headers: {},
            data: {
                adminId,
                password,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 418:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 테스트 계정 로그인
 */
const requestTestAccountSignInAPI = async (): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/demo-signin`,
            headers: {},
            data: {},
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 418:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 관리자 id 중복검사
 */
const getCheckDuplicateIdAPI = async (adminId: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/check-duplicate-id/${adminId}`,
            headers: {},
            data: {},
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 409:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 인증번호 발급
 */
const requestIssueSmsCodeAPI = async (phoneNumber: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/issue-smscode`,
            headers: {},
            data: {
                phoneNumber,
            },
        });

        if (response.status === 201) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 409:
                return { status: error.response.status };
            case 418:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 인증번호 검증
 */
const requestVerifySmsCodeAPI = async (phoneNumber: string, smsCode: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/verify-smscode`,
            headers: {},
            data: {
                phoneNumber,
                smsCode,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        console.log(error);
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 409:
                return { status: error.response.status };
            case 418:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

/**
 * 탭 잠금 새 계약 — 손실 시그널·기프티콘 결정을 서버가 따로 저장한다
 * (rappo docs/LOCK_SECTIONS_SERVER_SYNC_DESIGN.md v3). 이 헤더를 보낸 요청에만 새 응답이 온다.
 */
const LOCK_CONTRACT_HEADER = 'X-Rappo-Lock-Contract';
const LOCK_CONTRACT_VERSION = '3';

export /**
 * @method get
 * @brief 정산 비밀번호 조회
 * https://github.com/mustMoveProject/rappo-project/blob/develop/src/docs/Admin.md#%EC%A0%95%EC%82%B0-%EB%B9%84%EB%B0%80%EB%B2%88%ED%98%B8-%EC%A1%B0%ED%9A%8C
 */
const getCheckPayrollAuth = async (adminId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/payroll-auth/${adminId}`,
            /*
             * 잠금 조회는 **항상** 새 계약(3)을 묻는다. 응답 lockVersion 으로 서버가 아는지 판정한다.
             * 구서버(롤백)는 헤더를 무시하고 lockVersion 2 를 준다 — 그때는 지금 동작 그대로.
             */
            headers: { authorization: accessToken, [LOCK_CONTRACT_HEADER]: LOCK_CONTRACT_VERSION },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 415:
                return { status: error.response.status };
            case 409:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 정산 비밀번호 인증
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/Admin.md#%EC%A0%95%EC%82%B0-%EB%B9%84%EB%B0%80%EB%B2%88%ED%98%B8-%EC%9D%B8%EC%A6%9D
 */
const postCheckPayrollAuth = async (adminId: string, payrollPassword: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/check-payroll-auth`,
            headers: { authorization: accessToken },
            data: {
                adminId,
                payrollPassword,
            },
        });

        if (response.status === 200 || response.status === 201 || response.status === 415) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 415:
                return { status: error.response.status };
            case 409:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export type UpdateLockedSectionsOptions = {
    /** 새 계약 헤더를 붙인다. 지원 상태가 v3 일 때만 켠다 */
    contract?: boolean;
    /**
     * 새 계약 전용 — 이번에 바뀐 결정 키만. **저장 키**(opsBriefing·gifticon·scheduleCreate·scheduleEdit·
     * scheduleDelete)로 보낸다. 섹션 키(schedule.create 등)를 넣으면 서버가 400 으로 거절한다.
     */
    lockDecisions?: Partial<Record<DecisionStorageKey, boolean>>;
    /** 이관 전용 — 해당 키가 서버 미정일 때만 쓴다(결정 하나, lockedSections 없이) */
    lockDecisionsOnlyIfUnset?: boolean;
    /** 배경 요청(이관) — 500 이어도 경고창을 띄우지 않는다 */
    silent?: boolean;
};

/**
 * @method patch
 * @brief 탭 잠금 저장
 *
 * `lockedSections` 가 undefined 면 본문에서 뺀다(새 계약 이관 요청 전용).
 * 실패는 `{ status }` 로 돌려준다. 네트워크 오류(응답 없음)는 undefined 다.
 */
export const updateLockedSectionsAPI = async (
    adminId: string,
    lockedSections: string[] | undefined,
    accessToken: string,
    options: UpdateLockedSectionsOptions = {}
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    const data: Record<string, unknown> = {};
    if (lockedSections !== undefined) data.lockedSections = lockedSections;
    if (options.contract && options.lockDecisions && Object.keys(options.lockDecisions).length > 0) {
        data.lockDecisions = options.lockDecisions;
    }
    if (options.contract && options.lockDecisionsOnlyIfUnset) data.lockDecisionsOnlyIfUnset = true;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/locked-sections/${adminId}`,
            headers: options.contract
                ? { authorization: accessToken, [LOCK_CONTRACT_HEADER]: LOCK_CONTRACT_VERSION }
                : { authorization: accessToken },
            data,
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        switch (error.response?.status) {
            case 400:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 500:
                if (!options.silent) alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};
